import React, { useCallback, useEffect, useState } from 'react';
import { FieldType } from './field.types';
import { TextField } from '@mui/material';
import { EventRegistrationFormFieldOptions } from '../generated/api/dsv-public/model';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import { useFormValidation } from '../context/FormValidation';
import { useSingleForm } from '../context/SingleForm';

type DateFieldProps = FieldType & {
  options?: EventRegistrationFormFieldOptions;
};

export const DateField: React.FC<DateFieldProps> = ({
  required,
  fieldKey,
  name,
  defaultValue,
  description,
  onChangeFieldValue,
}) => {
  const [dateValue, setDateValue] = useState<Dayjs | null>(defaultValue ? dayjs(defaultValue) : null);
  const [isValid, setIsValid] = useState<boolean>(true);
  const [editDone, setEditDone] = useState<boolean>(false);
  const { showError, updateFieldValidation } = useFormValidation();
  const { formIndex } = useSingleForm();

  const setFieldValidity = useCallback(
    (newIsValid: boolean) => {
      setIsValid(newIsValid);
      updateFieldValidation(`${formIndex}-${fieldKey}`, newIsValid);
    },
    [updateFieldValidation, formIndex, fieldKey]
  );

  useEffect(() => {
    if (required && !dateValue) {
      setFieldValidity(!!defaultValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isError = (showError || editDone) && !isValid;

  const handleChange = useCallback(
    (date: Dayjs | null, inputValue?: string) => {
      if (date != null) {
        setDateValue(date);
        const parsedDate = dayjs(inputValue, 'DD.MM.YYYY', true);
        const isValid = Boolean(parsedDate.isValid() || (!inputValue && date));
        setFieldValidity(isValid);
        onChangeFieldValue(fieldKey, date.format('YYYY-MM-DD'));
      }
    },
    [setDateValue, onChangeFieldValue, setFieldValidity, fieldKey]
  );

  const handleBlur = () => {
    setEditDone(true);
  };

  return (
    <DatePicker
      label={name}
      inputFormat="DD.MM.YYYY"
      value={dateValue}
      onChange={handleChange}
      renderInput={params => (
        <TextField
          {...params}
          id={`${formIndex}-${fieldKey}`}
          error={isError}
          name={fieldKey}
          helperText={description}
          required={required}
          onBlur={handleBlur}
        />
      )}
    />
  );
};
